/**
 * Task Relation Types and Constants
 *
 * Relation types define how two tasks are linked.
 * For directional relations, only one row is stored per relation.
 * The "outgoing" label is shown on the source task, "incoming" on the target task.
 */

export const RELATION_TYPES = {
  RELATED_TO: 0,
  PREDECESSOR_SUCCESSOR: 1,
  BLOCKS_BLOCKED_BY: 2,
  PARENT_CHILD: 3,
  DUPLICATES: 4,
} as const

export type RelationType = (typeof RELATION_TYPES)[keyof typeof RELATION_TYPES]

export const RELATION_LABELS: Record<
  number,
  { outgoing: string; incoming: string }
> = {
  [RELATION_TYPES.RELATED_TO]: {
    outgoing: 'Related to',
    incoming: 'Related to',
  },
  [RELATION_TYPES.PREDECESSOR_SUCCESSOR]: {
    outgoing: 'Predecessor of',
    incoming: 'Successor of',
  },
  [RELATION_TYPES.BLOCKS_BLOCKED_BY]: {
    outgoing: 'Blocks',
    incoming: 'Blocked by',
  },
  [RELATION_TYPES.PARENT_CHILD]: {
    outgoing: 'Parent of',
    incoming: 'Child of',
  },
  [RELATION_TYPES.DUPLICATES]: {
    outgoing: 'Duplicates',
    incoming: 'Duplicated by',
  },
}

/**
 * Symmetric relation types where A→B and B→A are the same.
 * For these types, duplicate prevention must check both directions.
 */
export const SYMMETRIC_RELATION_TYPES: ReadonlyArray<RelationType> = [
  RELATION_TYPES.RELATED_TO,
  RELATION_TYPES.DUPLICATES,
]

/**
 * Directional relation types whose chains have transitive meaning. Cycles in
 * these graphs are logically invalid (e.g. A parent-of B parent-of A).
 */
export const DIRECTIONAL_RELATION_TYPES: ReadonlyArray<RelationType> = [
  RELATION_TYPES.PREDECESSOR_SUCCESSOR,
  RELATION_TYPES.BLOCKS_BLOCKED_BY,
  RELATION_TYPES.PARENT_CHILD,
]

export const USER_EDITABLE_RELATION_TYPES: ReadonlyArray<RelationType> = [
  RELATION_TYPES.RELATED_TO,
  RELATION_TYPES.PREDECESSOR_SUCCESSOR,
  RELATION_TYPES.BLOCKS_BLOCKED_BY,
  RELATION_TYPES.PARENT_CHILD,
  RELATION_TYPES.DUPLICATES,
]

export function isUserEditableRelationType(relationType: number): boolean {
  return (USER_EDITABLE_RELATION_TYPES as ReadonlyArray<number>).includes(
    relationType,
  )
}

export function isDirectionalRelationType(relationType: number): boolean {
  return (DIRECTIONAL_RELATION_TYPES as ReadonlyArray<number>).includes(
    relationType,
  )
}

interface RelationEdge {
  sourceTaskId: string
  targetTaskId: string
  relationType: number
}

/**
 * BFS check: returns true iff adding `source -> target` of `relationType`
 * would close a cycle in the existing same-type directed graph. Caller is
 * responsible for filtering out deleted relations.
 */
export function wouldCreateCycle(
  edges: ReadonlyArray<RelationEdge>,
  sourceTaskId: string,
  targetTaskId: string,
  relationType: number,
): boolean {
  if (sourceTaskId === targetTaskId) return true
  if (!isDirectionalRelationType(relationType)) return false

  const adjacency = new Map<string, Array<string>>()
  for (const edge of edges) {
    if (edge.relationType !== relationType) continue
    const list = adjacency.get(edge.sourceTaskId)
    if (list) list.push(edge.targetTaskId)
    else adjacency.set(edge.sourceTaskId, [edge.targetTaskId])
  }

  const visited = new Set<string>([targetTaskId])
  const queue: Array<string> = [targetTaskId]
  while (queue.length > 0) {
    const current = queue.shift()!
    const neighbors = adjacency.get(current)
    if (!neighbors) continue
    for (const next of neighbors) {
      if (next === sourceTaskId) return true
      if (!visited.has(next)) {
        visited.add(next)
        queue.push(next)
      }
    }
  }
  return false
}

/**
 * Get the display label for a relation from a given task's perspective.
 */
export function getRelationLabel(
  relationType: number,
  direction: 'outgoing' | 'incoming',
): string {
  return RELATION_LABELS[relationType]?.[direction] ?? 'Linked to'
}

/**
 * Check if a task is blocked (has incoming blocks_blocked_by relations).
 * Useful for showing a "blocked" badge on the task in list views.
 */
export function isBlockedRelation(
  relationType: number,
  direction: 'outgoing' | 'incoming',
): boolean {
  return (
    relationType === RELATION_TYPES.BLOCKS_BLOCKED_BY &&
    direction === 'incoming'
  )
}

/**
 * Check if a task is blocking other tasks (has outgoing blocks_blocked_by relations).
 */
export function isBlockingRelation(
  relationType: number,
  direction: 'outgoing' | 'incoming',
): boolean {
  return (
    relationType === RELATION_TYPES.BLOCKS_BLOCKED_BY &&
    direction === 'outgoing'
  )
}
